/**
 * Collects per-heartbeat quality metrics from two sources and returns a
 * single normalized shape to the caller:
 *
 *   1. SDK observable metrics (synchronous, written ~1/sec by
 *      ChimeObserverState.metricsDidReceive into a module-level ref).
 *      This is the cross-platform baseline — works on iOS Safari, Chrome,
 *      Firefox. Covers resolution / bitrate / loss / rtt / attendee count.
 *
 *   2. RTCPeerConnection.getStats() enrichment (async, per-heartbeat).
 *      Fills in fields the SDK's observable surface doesn't expose:
 *      `qualityLimitationReason` (instantaneous "none" / "cpu" / "bandwidth"
 *      / "other"), `qualityLimitationDurations` deltas (cumulative time
 *      spent in each limitation state since sender start), the negotiated
 *      video codec, inbound jitter, and inbound frames-dropped. Not all
 *      browsers expose all fields — older ones return `null` and the
 *      pipeline keeps working.
 *
 * The getStats call is wrapped in a 500ms timeout so a hanging SDK /
 * getStats promise never blocks the heartbeat. On timeout or error the
 * enrichment fields stay null; everything else in the metrics object
 * still flows.
 *
 * Codec capture: emitted on every sample. Codec is normally invariant
 * within a session, but WebRTC supports SDP renegotiation that can
 * in principle swap it. Per-sample emission keeps the implementation
 * in a single place — the alternative (emit-once with stored state plus
 * a separate top-level user-hash write path) is more code than the
 * single short string is worth. If multiple simulcast SSRCs disagree
 * on codec for the same publisher, the first non-null value wins and
 * `console.warn` fires once.
 *
 * Quality-limitation duration deltas: per-SSRC cumulative counters from
 * `qualityLimitationDurations` are diffed against a module-level prior
 * map keyed by SSRC. Per-key deltas are aggregated **across SSRCs by
 * max** and emitted as `outbound_video_quality_limitation_seconds`.
 * Max (not sum) because the audit question is "is any layer
 * experiencing pressure" — sum would scale with active layer count and
 * obscure wall-clock; per-SSRC dict would solve the per-layer question
 * but pulls scope from the per-SSRC aggregator restructuring elsewhere.
 * Max gives "the worst-affected layer's accumulated time in this state
 * per heartbeat interval" — direct enough for the immediate audit need
 * and trivially recoverable later. Negative deltas (counter reset /
 * clock weirdness) are clamped to 0 with a `console.warn`.
 *
 * First-sample / new-SSRC handling: deltas are computed only for SSRCs
 * that have a prior in the map. If zero SSRCs had priors (a legitimate
 * first sample of a session — see resetQualityCollectorState), the field
 * is emitted as `null`. If some SSRCs have priors and a new one appears
 * mid-session (e.g. simulcast layer activation), the new SSRC contributes
 * nothing this round but the field still ships with the deltas from the
 * SSRCs that have priors. Don't read `null` as "everything is fine" —
 * it specifically means "no signal this sample, will accumulate next".
 *
 * Multi-session reset: when the SDK's audioVideo facade is replaced
 * (session boundary, tab refresh), `useChimeObserver`'s effect cleanup
 * calls `resetQualityCollectorState()` to clear the prior map so the
 * first sample of the new session correctly emits `null` rather than
 * computing deltas against stale priors from the prior session's SSRCs.
 *
 * Cross-browser availability of the getStats enrichment: not universal
 * across older browser versions. The defensive walker treats any missing
 * field as `null` rather than throwing, so the heartbeat keeps shipping
 * even when an enrichment field is unavailable. Specific version
 * thresholds drift; defensive coding is what actually matters.
 */

import type { AudioVideoFacade } from "amazon-chime-sdk-js";

import { getLatestQualityMetrics } from "@platform/features/video/hooks/chimeObserverState";

// ─── Types ───────────────────────────────────────────────────────────

export type QualityLimitationDurationsDelta = {
  none: number;
  cpu: number;
  bandwidth: number;
  other: number;
};

export type CallQualityMetrics = {
  version: 1;
  timestamp: number;

  // Outbound video
  outbound_video_bitrate: number | null;
  outbound_video_fps: number | null;
  outbound_video_width: number | null;
  outbound_video_height: number | null;
  /**
   * From RTCStatsReport.outbound-rtp.qualityLimitationReason. Tells us
   * whether the encoder is downscaling because of CPU, bandwidth, or
   * something else. Null when the browser doesn't expose it.
   * Expected values: "none" | "cpu" | "bandwidth" | "other".
   */
  outbound_video_quality_limitation: string | null;
  /**
   * Negotiated video codec for the local sender, derived from
   * RTCStatsReport.codec entries via the outbound-rtp's codecId. Examples:
   * "VP9", "VP8", "H264", "AV1". Null when the browser doesn't expose
   * a codecId or when no outbound video sender is active yet.
   */
  outbound_video_codec: string | null;
  /**
   * Per-key deltas of qualityLimitationDurations (seconds) since the
   * prior heartbeat. Aggregated by max across simulcast SSRCs — see the
   * header comment for why max and not sum. Null on the first sample of
   * each session (no prior to subtract from), and null on browsers that
   * don't expose qualityLimitationDurations.
   */
  outbound_video_quality_limitation_seconds: QualityLimitationDurationsDelta | null;

  // Inbound video (aggregated across all remote streams)
  inbound_video_packet_loss: number | null;
  /** Max jitter in ms across inbound remote video tracks (s → ms). */
  inbound_video_jitter: number | null;
  /** Cumulative framesDropped across inbound remote video tracks. */
  inbound_video_frames_dropped: number | null;
  // Peak width/height across remote SSRCs — answers "am I receiving any HD?"
  // Not a per-remote breakdown: e.g. one HD active speaker plus low-res
  // thumbnails reports the HD value.
  inbound_video_width: number | null;
  inbound_video_height: number | null;
  inbound_video_bitrate: number | null;

  // Outbound audio
  outbound_audio_bitrate: number | null;

  // Inbound audio (aggregated)
  inbound_audio_packet_loss: number | null;
  inbound_audio_jitter: number | null;

  // Connection
  rtt_ms: number | null;
  available_outgoing_bitrate: number | null;
  available_incoming_bitrate: number | null;

  // Context
  connection_quality: string;
  attendee_count: number;

  // Connectivity
  missed_heartbeats: number;
  resumed_from_background: boolean;

  // Active devices
  active_audio_input_label: string | null;
  active_video_input_label: string | null;
};

// ─── getStats enrichment ─────────────────────────────────────────────

const STATS_TIMEOUT_MS = 500;

type StatsEnrichment = {
  outbound_video_quality_limitation: string | null;
  outbound_video_codec: string | null;
  outbound_video_quality_limitation_seconds: QualityLimitationDurationsDelta | null;
  inbound_video_jitter: number | null;
  inbound_video_frames_dropped: number | null;
};

const EMPTY_ENRICHMENT: StatsEnrichment = {
  outbound_video_quality_limitation: null,
  outbound_video_codec: null,
  outbound_video_quality_limitation_seconds: null,
  inbound_video_jitter: null,
  inbound_video_frames_dropped: null,
};

// Module-level prior-durations state, keyed by SSRC string. Mirrors the
// existing module-level pattern in chimeObserverState.ts (latestQualityMetrics).
// Reset across session boundaries via resetQualityCollectorState() — wired into
// useChimeObserver's effect cleanup (see header comment for rationale).
const priorDurations = new Map<string, QualityLimitationDurationsDelta>();

/**
 * Clear all session-scoped state held by the collector. Call from the
 * audioVideo-bound effect's cleanup so that the first sample of the next
 * session correctly emits `outbound_video_quality_limitation_seconds: null`
 * rather than diffing against stale priors from the prior session's SSRCs.
 */
export function resetQualityCollectorState(): void {
  priorDurations.clear();
}

/**
 * Race the stats call against a timeout so a hung RTCPeerConnection
 * never stalls the heartbeat. Rejects on timeout; caller catches and
 * degrades.
 */
async function getStatsWithTimeout(
  audioVideo: AudioVideoFacade,
): Promise<RTCStatsReport> {
  return Promise.race<RTCStatsReport>([
    audioVideo.getRTCPeerConnectionStats(),
    new Promise<RTCStatsReport>((_, reject) =>
      setTimeout(() => reject(new Error("getRTCPeerConnectionStats timeout")), STATS_TIMEOUT_MS),
    ),
  ]);
}

const DURATION_KEYS = ["none", "cpu", "bandwidth", "other"] as const;

function readDurations(raw: unknown): QualityLimitationDurationsDelta | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const out: QualityLimitationDurationsDelta = {
    none: 0,
    cpu: 0,
    bandwidth: 0,
    other: 0,
  };
  let saw = false;
  for (const k of DURATION_KEYS) {
    const v = r[k];
    if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = v;
      saw = true;
    }
  }
  return saw ? out : null;
}

/**
 * Walk an RTCStatsReport and extract the fields the SDK observable
 * metrics don't surface. Written defensively — every browser exposes a
 * slightly different subset of fields and we treat any missing property
 * as `null` rather than throwing.
 *
 * For `qualityLimitationReason` across multiple outbound-rtp entries
 * (simulcast sends three), prefer any non-"none" signal: if any layer
 * reports "cpu" or "bandwidth", that's the diagnostic-valuable value.
 * Layers reporting "none" don't override.
 *
 * For codec, take the first non-null mimeType across SSRCs. If a later
 * SSRC reports a different codec, log once and keep the first — codec
 * mismatch is a real anomaly to surface, not a stop signal.
 *
 * For qualityLimitationDurations, compute per-SSRC deltas against the
 * module-level priorDurations map and aggregate by max across SSRCs.
 * See the file header for why max and not sum, and for the
 * null-when-zero-priors convention.
 */
function extractFromStatsReport(report: RTCStatsReport): StatsEnrichment {
  let qualityLimitation: string | null = null;
  let jitterMs: number | null = null;
  let framesDropped: number | null = null;

  // Codec lookup table built from RTCStatsReport.codec entries.
  const codecsById = new Map<string, string>();
  let codec: string | null = null;
  let codecMismatchWarned = false;

  // Per-SSRC durations seen in this walk (for max aggregation across SSRCs).
  // We only keep deltas for SSRCs that had a prior; SSRCs with no prior
  // contribute nothing to this sample but their current value gets stored
  // in priorDurations for next round.
  const sampleDeltas: QualityLimitationDurationsDelta[] = [];

  try {
    // First pass: build codecId → mimeType lookup. Cheap and forward-only;
    // the iteration order is implementation-defined so we can't assume codec
    // entries appear before the outbound-rtp entries that reference them.
    report.forEach((entry) => {
      try {
        if (!entry || typeof entry !== "object") return;
        const e = entry as Record<string, unknown>;
        if (e.type === "codec" && typeof e.id === "string") {
          const mimeType = typeof e.mimeType === "string" ? e.mimeType : null;
          if (mimeType) codecsById.set(e.id, mimeType);
        }
      } catch {
        // Skip malformed entry.
      }
    });

    // Second pass: extract enrichment fields.
    report.forEach((entry) => {
      // Per-entry try so one malformed stats row doesn't abort the walk.
      try {
        if (!entry || typeof entry !== "object") return;
        const e = entry as Record<string, unknown>;

        if (e.type === "outbound-rtp" && e.kind === "video") {
          // qualityLimitationReason — prefer any non-"none" signal.
          const reason = e.qualityLimitationReason;
          if (typeof reason === "string") {
            if (qualityLimitation === null || qualityLimitation === "none") {
              qualityLimitation = reason;
            }
          }

          // Codec — first non-null wins; warn once on cross-SSRC mismatch.
          const codecId = typeof e.codecId === "string" ? e.codecId : null;
          if (codecId) {
            const mimeType = codecsById.get(codecId);
            if (mimeType) {
              const stripped = mimeType.replace(/^video\//i, "");
              if (codec === null) {
                codec = stripped;
              } else if (codec !== stripped && !codecMismatchWarned) {
                console.warn(
                  `[collectCallQualityMetrics] codec mismatch across simulcast SSRCs: ${codec} vs ${stripped}; keeping ${codec}`,
                );
                codecMismatchWarned = true;
              }
            }
          }

          // qualityLimitationDurations — per-SSRC delta, accumulated for max.
          const ssrcRaw = e.ssrc;
          const ssrcKey =
            typeof ssrcRaw === "number" || typeof ssrcRaw === "string"
              ? String(ssrcRaw)
              : null;
          const current = readDurations(e.qualityLimitationDurations);
          if (ssrcKey !== null && current !== null) {
            const prior = priorDurations.get(ssrcKey);
            if (prior) {
              const delta: QualityLimitationDurationsDelta = {
                none: 0,
                cpu: 0,
                bandwidth: 0,
                other: 0,
              };
              let negative = false;
              for (const k of DURATION_KEYS) {
                const d = current[k] - prior[k];
                if (d < 0) {
                  negative = true;
                  delta[k] = 0;
                } else {
                  delta[k] = d;
                }
              }
              if (negative) {
                console.warn(
                  `[collectCallQualityMetrics] negative qualityLimitationDurations delta on ssrc ${ssrcKey} (counter reset?); clamped to 0`,
                );
              }
              sampleDeltas.push(delta);
            }
            // Always update the prior for next round, regardless of whether
            // this SSRC contributed to this sample.
            priorDurations.set(ssrcKey, current);
          }
        } else if (e.type === "inbound-rtp" && e.kind === "video") {
          // Jitter is reported in seconds — convert to ms for consistency
          // with rtt_ms elsewhere in the metrics object.
          if (typeof e.jitter === "number") {
            const ms = e.jitter * 1000;
            jitterMs = jitterMs === null ? ms : Math.max(jitterMs, ms);
          }
          if (typeof e.framesDropped === "number") {
            framesDropped = (framesDropped ?? 0) + e.framesDropped;
          }
        }
      } catch {
        // Skip this entry; continue iterating.
      }
    });
  } catch {
    // forEach itself threw (shouldn't happen on a real RTCStatsReport);
    // return what we have so far.
  }

  // Max-across-SSRCs aggregation for the duration deltas. If no SSRC had
  // a prior (legitimate first sample of a session, or browser doesn't
  // expose qualityLimitationDurations at all), emit null.
  let durationsDelta: QualityLimitationDurationsDelta | null = null;
  if (sampleDeltas.length > 0) {
    const agg: QualityLimitationDurationsDelta = {
      none: 0,
      cpu: 0,
      bandwidth: 0,
      other: 0,
    };
    for (const d of sampleDeltas) {
      for (const k of DURATION_KEYS) {
        if (d[k] > agg[k]) agg[k] = d[k];
      }
    }
    durationsDelta = agg;
  }

  return {
    outbound_video_quality_limitation: qualityLimitation,
    outbound_video_codec: codec,
    outbound_video_quality_limitation_seconds: durationsDelta,
    inbound_video_jitter: jitterMs,
    inbound_video_frames_dropped: framesDropped,
  };
}

/**
 * Pull the getStats-only fields. Safe to call on any platform — returns
 * the empty enrichment shape on timeout, missing SDK method, or any
 * other failure.
 */
async function readRTCStatsEnrichment(
  audioVideo: AudioVideoFacade | null,
): Promise<StatsEnrichment> {
  if (!audioVideo || typeof audioVideo.getRTCPeerConnectionStats !== "function") {
    return EMPTY_ENRICHMENT;
  }
  try {
    const report = await getStatsWithTimeout(audioVideo);
    if (!report) return EMPTY_ENRICHMENT;
    return extractFromStatsReport(report);
  } catch {
    // Timeout, SDK unavailable (older iOS Safari), peer connection not
    // ready, malformed report — all degrade to nulls. The rest of the
    // metrics object still ships.
    return EMPTY_ENRICHMENT;
  }
}

// ─── Main ────────────────────────────────────────────────────────────

export async function collectCallQualityMetrics(
  context: {
    connectionQuality: string;
    attendeeCount: number;
    missedHeartbeats: number;
    resumedFromBackground: boolean;
    activeAudioInputLabel: string | null;
    activeVideoInputLabel: string | null;
  },
  audioVideo: AudioVideoFacade | null = null,
): Promise<CallQualityMetrics | null> {
  const raw = getLatestQualityMetrics();
  if (!raw) return null;

  const enrichment = await readRTCStatsEnrichment(audioVideo);

  return {
    version: 1,
    timestamp: Date.now(),
    ...raw,
    // Enrichment overrides the null stubs in raw (see chimeObserverState.ts
    // metricsDidReceive, which intentionally emits these as null because
    // the SDK observable surface doesn't expose them).
    ...enrichment,
    connection_quality: context.connectionQuality,
    attendee_count: context.attendeeCount,
    missed_heartbeats: context.missedHeartbeats,
    resumed_from_background: context.resumedFromBackground,
    active_audio_input_label: context.activeAudioInputLabel,
    active_video_input_label: context.activeVideoInputLabel,
  };
}
